import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Download, FileUp } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { gruppoColor } from "@/lib/race";
import PilotAvatar from "@/components/PilotAvatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { nome: "", cognome: "", vettura: "", gruppo: "", categoria: "", prezzo: "", numero: "", foto_url: "" };

export default function PilotsTab() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [replaceAll, setReplaceAll] = useState(false);

  const preview = useMemo(() => {
    if (!csvText.trim()) return null;
    const lines = csvText.trim().split(/\r?\n/);
    const headers = lines[0].split(/[,;]/).map((h) => h.trim().toLowerCase());
    return { headers, rows: lines.slice(1, 6).map((l) => l.split(/[,;]/)), total: Math.max(0, lines.length - 1) };
  }, [csvText]);

  const readCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setCsvText(String(r.result));
    r.readAsText(f);
  };

  const doImport = async () => {
    try {
      const { data } = await api.post("/admin/pilots/import", { csv_text: csvText, replace: replaceAll });
      toast.success(`Importati ${data.importati} piloti${data.errori?.length ? ` — ${data.errori.length} righe scartate` : ""}`);
      setImportOpen(false);
      setCsvText("");
      qc.invalidateQueries({ queryKey: ["pilots"] });
    } catch (e) {
      toast.error(errMsg(e, "Importazione fallita"));
    }
  };

  const exportCsv = async () => {
    const res = await api.get("/admin/pilots/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "piloti_monti_iblei.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Lista piloti esportata");
  };

  const { data: pilots = [] } = useQuery({
    queryKey: ["pilots", "", "", ""],
    queryFn: async () => (await api.get("/pilots")).data,
  });

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ nome: p.nome, cognome: p.cognome, vettura: p.vettura, gruppo: p.gruppo, categoria: p.categoria || "", prezzo: p.prezzo, numero: p.numero, foto_url: p.foto_url || "" });
    setOpen(true);
  };

  const uploadFoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2000000) {
      toast.error("Foto troppo grande (max 2 MB)");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload-asset", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, foto_url: data.url }));
      toast.success("Foto caricata sullo storage interno");
    } catch (err) {
      toast.error(errMsg(err, "Upload foto fallito"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, prezzo: parseFloat(form.prezzo), numero: form.numero ? parseInt(form.numero) : null };
    try {
      if (editing) {
        await api.put(`/admin/pilots/${editing.id}`, payload);
        toast.success("Pilota aggiornato");
      } else {
        await api.post("/admin/pilots", payload);
        toast.success("Pilota iscritto alla gara");
      }
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["pilots"] });
    } catch (err) {
      toast.error(errMsg(err, "Salvataggio fallito"));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Eliminare ${p.nome} ${p.cognome}?`)) return;
    await api.delete(`/admin/pilots/${p.id}`);
    toast.success("Pilota eliminato");
    qc.invalidateQueries({ queryKey: ["pilots"] });
  };

  const inputCls =
    "w-full rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-wrap justify-end gap-2">
        <button
          data-testid="admin-export-pilots-button"
          onClick={exportCsv}
          className="flex items-center gap-2 rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest uppercase"
        >
          <Download className="w-4 h-4" /> Esporta Lista Piloti
        </button>
        <button
          data-testid="admin-import-pilots-button"
          onClick={() => setImportOpen(true)}
          className="flex items-center gap-2 rounded-lg border border-[#3B82F6] text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest uppercase"
        >
          <FileUp className="w-4 h-4" /> Importa da File (CSV/Excel)
        </button>
        <button
          data-testid="admin-add-pilot-button"
          onClick={openNew}
          className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
        >
          <Plus className="w-4 h-4" /> Nuovo Pilota
        </button>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#3F3F4C] bg-[#1A1A1E]">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-[#3F3F4C] bg-[#121214]">
              {["#", "Pilota", "Vettura", "Gruppo", "Prezzo", "Punti", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pilots.map((p, i) => (
              <tr key={p.id} className={`border-b border-[#242429] ${i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"}`}>
                <td className="px-4 py-2.5 font-time font-bold" style={{ color: gruppoColor(p.gruppo) }}>{p.numero}</td>
                <td className="px-4 py-2.5 font-body-sport font-semibold text-white text-sm">
                  <div className="flex items-center gap-2">
                    <PilotAvatar pilot={p} size={32} />
                    <span>{p.nome} {p.cognome}</span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-gray-400">{p.vettura}</td>
                <td className="px-4 py-2.5 font-body-sport text-xs font-bold" style={{ color: gruppoColor(p.gruppo) }}>{p.gruppo}</td>
                <td className="px-4 py-2.5 font-time text-[#FFB703] text-sm">{p.prezzo}</td>
                <td className="px-4 py-2.5 font-time text-white text-sm">{p.punti_totali}</td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <button
                      data-testid={`admin-edit-pilot-${p.id}`}
                      onClick={() => openEdit(p)}
                      className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#FFB703] hover:border-[#FFB703] transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      data-testid={`admin-delete-pilot-${p.id}`}
                      onClick={() => remove(p)}
                      className="p-1.5 rounded border border-[#3F3F4C] text-gray-400 hover:text-[#FF1E27] hover:border-[#FF1E27] transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1A1A1E] border-[#3F3F4C]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-racing text-white uppercase tracking-wide">
              {editing ? "Modifica Pilota" : "Nuovo Pilota"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="pilot-form-nome" className={inputCls} placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} required />
              <input data-testid="pilot-form-cognome" className={inputCls} placeholder="Cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} required />
            </div>
            <input data-testid="pilot-form-vettura" className={inputCls} placeholder="Vettura / Scuderia" value={form.vettura} onChange={(e) => setForm({ ...form, vettura: e.target.value })} required />
            <div className="grid grid-cols-3 gap-3">
              <input data-testid="pilot-form-gruppo" className={inputCls} placeholder="Classe vettura (es. E2SC-SS)" value={form.gruppo} onChange={(e) => setForm({ ...form, gruppo: e.target.value })} required />
              <input data-testid="pilot-form-prezzo" type="number" step="0.5" className={inputCls} placeholder="Prezzo" value={form.prezzo} onChange={(e) => setForm({ ...form, prezzo: e.target.value })} required />
              <input data-testid="pilot-form-numero" type="number" className={inputCls} placeholder="N° gara" value={form.numero} onChange={(e) => setForm({ ...form, numero: e.target.value })} />
            </div>
            <div className="flex items-center gap-3">
              {form.foto_url ? (
                <img src={form.foto_url} alt="Foto pilota" data-testid="pilot-form-foto-preview" className="w-12 h-12 rounded-lg object-cover border border-[#3F3F4C]" />
              ) : (
                <div className="w-12 h-12 rounded-lg border border-dashed border-[#3F3F4C] flex items-center justify-center text-gray-600 text-[8px] font-body-sport font-bold tracking-widest">
                  FOTO
                </div>
              )}
              <label className="flex-1 cursor-pointer rounded-lg border border-[#3B82F6]/60 text-[#3B82F6] hover:bg-[#3B82F6]/10 transition-colors px-3 py-2.5 min-h-[44px] flex items-center justify-center text-[11px] font-body-sport font-bold tracking-widest uppercase">
                {form.foto_url ? "Sostituisci Foto" : "Carica Foto Reale (max 2MB)"}
                <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden" data-testid="pilot-form-foto" onChange={uploadFoto} />
              </label>
              {form.foto_url && (
                <button type="button" data-testid="pilot-form-foto-remove" onClick={() => setForm({ ...form, foto_url: "" })} className="p-2 text-gray-500 hover:text-[#FF1E27] transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-600 font-body-sport">
              Senza foto reale viene mostrato l'avatar stilizzato (casco con numero e colore categoria) ovunque: mercato, dashboard, social card.
            </p>
            <button
              data-testid="pilot-form-submit"
              type="submit"
              className="w-full rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-40 disabled:cursor-not-allowed transition-colors py-2.5 font-racing font-bold text-xs tracking-widest text-white uppercase"
            >
              Salva Pilota
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="bg-[#1A1A1E] border-[#3F3F4C] max-w-lg" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-racing text-white uppercase tracking-wide">
              Importa Piloti da CSV
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-gray-400 font-body-sport">
              Formato colonne: <span className="font-time text-[#FFB703]">numero, nome, cognome, vettura, gruppo, prezzo</span>{" "}
              (da Excel: File → Salva come CSV).
            </p>
            <input
              type="file"
              accept=".csv,.txt"
              data-testid="import-file-input"
              onChange={readCsvFile}
              className="block w-full text-[11px] text-gray-400 file:mr-2 file:rounded-lg file:border-0 file:bg-[#FF1E27] file:px-3 file:py-1.5 file:text-[10px] file:font-bold file:text-white file:uppercase file:tracking-widest hover:file:bg-[#CC1118] file:cursor-pointer"
            />
            {preview && (
              <div className="rounded-lg border border-[#3F3F4C] overflow-hidden">
                <p className="px-3 py-2 text-[10px] font-body-sport font-bold tracking-widest text-gray-500 uppercase bg-[#121214] border-b border-[#3F3F4C]">
                  Anteprima mappatura — {preview.total} piloti rilevati
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="border-b border-[#242429]">
                        {preview.headers.map((h) => (
                          <th key={h} className="px-2 py-1.5 text-left font-time text-[#FFB703]">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => (
                        <tr key={`row-${i}-${r.join("|")}`} className="border-b border-[#242429]">
                          {r.map((c, j) => (
                            <td key={`${j}-${c}`} className="px-2 py-1.5 text-gray-300 font-body-sport">{c}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-gray-300 font-body-sport cursor-pointer">
              <input
                type="checkbox"
                data-testid="import-replace-checkbox"
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                className="accent-[#FF1E27] w-4 h-4"
              />
              Sostituisci l'intera lista esistente (azzera piloti e tempi)
            </label>
            <button
              data-testid="import-confirm-button"
              onClick={doImport}
              disabled={!csvText.trim()}
              className="w-full rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] disabled:opacity-40 transition-colors py-2.5 min-h-[44px] font-racing font-bold text-xs tracking-widest text-white uppercase"
            >
              Carica e Popola Database Piloti
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
